import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  PROJECT_TEAM_ROLES,
  useAddProjectTeam,
  type ProjectTeamRole,
} from '@/api/projects';
import { useMyTeams } from '@/api/teams';

interface AddTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** Team ids already attached to the project — filtered out of the picker. */
  existingTeamIds: string[];
}

export function AddTeamDialog({
  open,
  onOpenChange,
  projectId,
  existingTeamIds,
}: AddTeamDialogProps) {
  const { show } = useToast();
  const { data: teams = [], isLoading } = useMyTeams();
  const [teamId, setTeamId] = useState('');
  const [role, setRole] = useState<ProjectTeamRole>('member');

  const available = useMemo(
    () => teams.filter((t) => !existingTeamIds.includes(t.id)),
    [teams, existingTeamIds],
  );

  const add = useAddProjectTeam(projectId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Team added to project' });
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not add team' }),
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      setTeamId('');
      setRole('member');
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add team</DialogTitle>
          <DialogDescription>
            Grant one of your teams access to this project.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field
              label="Team"
              required
              description={
                !isLoading && available.length === 0
                  ? 'No available teams to add. Create a new team first.'
                  : undefined
              }
            >
              {() => (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger disabled={available.length === 0}>
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.team_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Role" required>
              {() => (
                <Select value={role} onValueChange={(v) => setRole(v as ProjectTeamRole)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TEAM_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label} — {r.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!teamId || add.isPending}
            onClick={() => add.mutate({ team_id: teamId, role })}
          >
            {add.isPending ? 'Adding…' : 'Add team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
